import React from "react";
import { 
  AppBar, 
  Toolbar, 
  Box, 
  IconButton, 
  Typography, 
  Avatar,
  Tooltip,
  useTheme
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import LogoutButton from '../../../components/common/Button/LogoutButton';
import { useAuth } from '../../../contexts/AuthContext';
import { Link } from "react-router-dom";

interface HeaderProps {
  onDrawerToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onDrawerToggle }) => {
  const { user, isUserInGroup } = useAuth();
  const theme = useTheme();

  // Define colors
  const darkBlue = "#0A4D68";
  const accentYellow = "#F9D949";
  const accentGreen = "#088395";

  // Get user initial for avatar
  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    } else if (user?.given_name) {
      return user.given_name.charAt(0).toUpperCase();
    } else if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U"; // Default fallback
  };
  
  // Format user name for display
  const getDisplayName = () => {
    if (user?.name) {
      return user.name.split(" ")[0]; // Just the first name
    } else if (user?.given_name) {
      return user.given_name;
    }
    return "User";
  };

  // Get user role for display
  const getUserRole = () => {
    if (isUserInGroup("Admins")) {
      return "Administrator";
    } else if (isUserInGroup("Editors")) {
      return "Editor";
    } else if (isUserInGroup("Volunteers")) {
      return "Volunteer";
    }
    return "User";
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Left section with menu button */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={onDrawerToggle}
            sx={{ 
              mr: 1, 
              display: { sm: "none" },
              color: darkBlue
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="subtitle2"
            sx={{ 
              color: darkBlue,
              fontWeight: 600,
              display: { xs: 'none', md: 'block' } 
            }}
          >
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
        </Box>

        {/* Center logo */}
        <Box 
          component={Link}
          to="/admin/dashboard"
          sx={{ 
            position: 'absolute', 
            left: '50%', 
            top: '50%', 
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <img 
            src="/hbi_logo.svg" 
            alt="Hope Beyond International Logo" 
            style={{ 
              height: '45px'
            }} 
          />
        </Box>

        {/* Right section with user info and logout */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* User info section */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mr: 2,
              p: 0.5,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'rgba(10, 77, 104, 0.04)'
              }
            }}
          >
            <Tooltip title={user?.email || ""}>
              <Avatar 
                sx={{ 
                  width: 36, 
                  height: 36, 
                  bgcolor: darkBlue,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}
              >
                {getUserInitial()}
              </Avatar>
            </Tooltip>
            
            <Box sx={{ ml: 1.5, display: { xs: 'none', sm: 'block' } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: darkBlue,
                  lineHeight: 1.2
                }}
              >
                {getDisplayName()}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.2
                }}
              >
                {getUserRole()}
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Logout">
            <Box>
              <LogoutButton color={darkBlue} hoverColor={accentGreen} />
            </Box>
          </Tooltip>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;